import { inv } from "mathjs";
import { ResourceOrder } from "../Project";

export interface RealBookingRow {
  phase: string;
  job: string;
  id: number;
  resource_order: number | null;
  name: string;
  number?: string;
  tag?: number;
  tagName?: string;
  type: "HUMAN" | "CREW" | "RESOURCE" | "CREW_MEMBER";
}

export interface RealBookingStruct {
  [phase: string]: { [job: string]: RealBookingRow[] };
}

export interface ResourceOrderStruct {
  [phase: string]: { [job: string]: (ResourceOrder & { tagName: string })[] };
}

export type ProcessedBooking = RealBookingRow & {
  isOrder: false;
  combinationId: string;
  slot: number | null;
};

export type ProcessedOrder = ResourceOrder & {
  isOrder: true;
  combinationId: string;
  tagName: string;
  filled: number;
  slot: number;
};

export interface CombinedStruct {
  [phase: string]: { [job: string]: (ProcessedOrder | ProcessedBooking)[] };
}

export class BookingOrderCombiner {
  static combine(realBookings: RealBookingStruct, resourceOrders: ResourceOrderStruct): CombinedStruct {
    const combined: CombinedStruct = {};

    const phaseSet = new Set<string>([...Object.keys(realBookings), ...Object.keys(resourceOrders)]);

    for (const phase of phaseSet) {
      const phaseBookings = realBookings[phase] || {};
      const phaseOrders = resourceOrders[phase] || {};

      const jobSet = new Set<string>([...Object.keys(phaseBookings), ...Object.keys(phaseOrders)]);

      const phaseResult: { [job: string]: (ProcessedOrder | ProcessedBooking)[] } = {};

      for (const job of jobSet) {
        const jobBookings = phaseBookings[job] || [];
        const jobOrders = phaseOrders[job] || [];

        const merged = this.mergeJob(jobBookings, jobOrders);

        if (merged.length > 0) {
          phaseResult[job] = merged;
        }
      }

      if (Object.keys(phaseResult).length > 0) {
        combined[phase] = phaseResult;
      }
    }

    return combined;
  }

  private static mergeJob(bookings: RealBookingRow[], orders: ResourceOrder[]): (ProcessedOrder | ProcessedBooking)[] {
    // Map orders by id and initialise filled counters
    const orderMap = new Map<number, ProcessedOrder>();
    for (const order of orders) {
      // Assumption: deleted orders are not considered for fulfilment
      if (order.deleted) continue;

      orderMap.set(order.id, {
        ...order,
        isOrder: true,
        combinationId: "",
        filled: 0,
        slot: 0,
      });
    }

    // Group bookings by resource_order id, and separate free/orphaned bookings
    const bookingsByOrderId = new Map<number, RealBookingRow[]>();
    const freeBookings: RealBookingRow[] = [];
    const orphanedBookings: RealBookingRow[] = [];

    for (const booking of bookings) {
      const orderId = booking.resource_order;
      if (orderId == null) {
        freeBookings.push(booking);
        continue;
      }

      const order = orderMap.get(orderId);
      if (!order) {
        // Booking references an order that does not exist (or was deleted)
        orphanedBookings.push(booking);
        continue;
      }

      let list = bookingsByOrderId.get(orderId);
      if (!list) {
        list = [];
        bookingsByOrderId.set(orderId, list);
      }
      list.push(booking);
    }

    const processedBookings: ProcessedBooking[] = [];

    const toProcessedBooking = (b: RealBookingRow, slot: number | null): ProcessedBooking => ({
      ...b,
      combinationId: "rb" + b.id,
      isOrder: false as const,
      slot,
    });

    // Bookings without resource_order just pass through with slot = null
    for (const b of freeBookings) {
      processedBookings.push(toProcessedBooking(b, null));
    }

    // Bookings that reference non-existing orders; keep them, no slot
    for (const b of orphanedBookings) {
      processedBookings.push(toProcessedBooking(b, null));
    }

    // Process bookings that are tied to valid orders
    for (const [orderId, list] of bookingsByOrderId) {
      const order = orderMap.get(orderId)!;

      // Deterministic order: sort by name, then id as tie-breaker
      list.sort((a, b) => {
        const nameCmp = a.name.localeCompare(b.name);
        if (nameCmp !== 0) return nameCmp;
        return a.id - b.id;
      });

      for (const booking of list) {
        if (order.filled < order.amount) {
          const slot = order.filled + 1;
          order.filled = slot;
          processedBookings.push(toProcessedBooking(booking, slot));
        } else {
          // Overflow booking: keep it, but it does not fill a slot
          processedBookings.push(toProcessedBooking(booking, null));
        }
      }
    }

    // Only keep open orders (filled < amount)
    const openOrders: ProcessedOrder[] = [];
    for (const order of orderMap.values()) {
      if (order.filled < order.amount) {
        for (let slot = order.filled + 1; slot <= order.amount; slot++) {
          openOrders.push({ ...order, slot, combinationId: "ro_" + (order.id ?? order.tag) + "_" + slot });
        }
      }
    }

    const result = [...processedBookings, ...openOrders];

    // Optional: sort bookings by name (for stable output), then id
    result.sort((a, b) => {
      if ((a.type === "CREW" && !a.isOrder && b.type === "CREW_MEMBER") || (b.type === "CREW" && !b.isOrder && a.type === "CREW_MEMBER")) {
        const inverter = a.type === "CREW" ? 1 : -1;
        const crew = a.type === "CREW" ? a : b;
        const member = a.type === "CREW" ? b : a;

        console.log(crew, member);

        if (crew.name === member.crew) return -1 * inverter;

        return crew.name.localeCompare(member.crew) * inverter;
      }
      if (a.type === "CREW_MEMBER" && b.type === "CREW_MEMBER") {
        if (a.crew === b.crew) {
          return a.name.localeCompare(b.name);
        } else {
          return a.crew.localeCompare(b.crew);
        }
      }
      if (a.type > b.type) return 1;
      if (a.type < b.type) return -1;
      if (a.type === "RESOURCE") {
        const tagCmp = a.tagName.localeCompare(b.tagName);
        if (tagCmp !== 0) return tagCmp;
        if (!!a.slot && !b.slot) return -1;
        if (!a.slot && !!b.slot) return 1;
        if (!a.slot && !b.slot) return a.name.localeCompare(b.name);
        const cmp = (a.isOrder ? (a.id ?? 0) : a.resource_order) - (b.isOrder ? (b.id ?? 0) : b.resource_order);
        if (cmp !== 0) return cmp;
        return a.slot - b.slot;
      } else {
        if (!!a.slot && !b.slot) return -1;
        if (!a.slot && !!b.slot) return 1;
        if (!a.slot && !b.slot) return a.name.localeCompare(b.name);
        const cmp = (a.isOrder ? (a.id ?? 0) : a.resource_order) - (b.isOrder ? (b.id ?? 0) : b.resource_order);
        if (cmp !== 0) return cmp;
        return a.slot - b.slot;
      }
    });

    return result;
  }
}
